import { c as _c } from "react/compiler-runtime";
import { Skeleton, Table } from '@systemeio/components';
import React, { type JSX, type ReactNode } from 'react';
import { Tooltip } from 'shared/components';
import { twJoin } from 'tailwind-merge';
interface TableDataInterface<T> {
  items: T[];
}
type AlignType = 'justify-center' | 'justify-end' | 'justify-start';
interface TabSubHeaderDefinitionInterface<T> {
  align?: AlignType;
  onRender?: (instance: T) => JSX.Element;
  onRenderSmall?: (instance: T) => JSX.Element;
  title?: string | JSX.Element;
  widthPercent?: number;
}
export interface TabColumnDefinitionInterface<T> {
  header: string | JSX.Element;
  key: string;
  shouldRender?: boolean;
  subHeaders: TabSubHeaderDefinitionInterface<T>[];
  subHeaderWidthPercent?: number[];
  widthPercent?: number;
}
export interface TableProps<T, K> {
  columns: TabColumnDefinitionInterface<T>[];
  data?: K;
}
const SKELETON_ROW_COUNT = 6;
const getWidth = (widthPercent?: number) => widthPercent ?? 25;
function TabTable(t0) {
  const $ = _c(19);
  const {
    columns,
    data
  } = t0;
  let t1;
  let visibleColumns;
  if ($[0] !== columns) {
    visibleColumns = columns.filter(_temp);
    t1 = visibleColumns.reduce(_temp2, 0);
    $[0] = columns;
    $[1] = t1;
    $[2] = visibleColumns;
  } else {
    t1 = $[1];
    visibleColumns = $[2];
  }
  const totalWidth = t1;
  let t2;
  if ($[3] !== totalWidth || $[4] !== visibleColumns) {
    t2 = <TableHeader columns={visibleColumns} totalWidth={totalWidth} />;
    $[3] = totalWidth;
    $[4] = visibleColumns;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== data || $[7] !== visibleColumns) {
    t3 = <MobileRows columns={visibleColumns} data={data} />;
    $[6] = data;
    $[7] = visibleColumns;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== data || $[10] !== totalWidth || $[11] !== visibleColumns) {
    t4 = <DesktopRows columns={visibleColumns} data={data} totalWidth={totalWidth} />;
    $[9] = data;
    $[10] = totalWidth;
    $[11] = visibleColumns;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t3 || $[14] !== t4) {
    t5 = <div className="overflow-auto">{t3}{t4}</div>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t2 || $[17] !== t5) {
    t6 = <div className="bg-bg-primary border-border-100 flex w-full flex-col overflow-hidden rounded-xl border">{t2}{t5}</div>;
    $[16] = t2;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}
function _temp2(total, column_0) {
  return total + getWidth(column_0.widthPercent);
}
function _temp(column) {
  return column.shouldRender !== false;
}
function TableHeader(t0) {
  const $ = _c(7);
  const {
    columns,
    totalWidth
  } = t0;
  let t1;
  if ($[0] !== columns || $[1] !== totalWidth) {
    let t2;
    if ($[3] !== totalWidth) {
      t2 = column => {
        const hasSubHeaders = column.subHeaders.some(_temp3);
        const subHeadersWidth = column.subHeaders.reduce(_temp4, 0);
        return <div key={column.key} className="border-table-cell-border text-table-header-text flex min-w-0 flex-col border-r text-sm last:border-r-0" style={{
          flex: `${getWidth(column.widthPercent) / totalWidth * 100} 1 0%`
        }}><div className={twJoin("flex w-full min-w-0 flex-1 items-center px-4 py-3 font-medium uppercase", hasSubHeaders && "border-table-cell-border justify-center border-b")}>{typeof column.header === "string" ? <Tooltip mode="overflow" label={column.header}><span className="w-full truncate">{column.header}</span></Tooltip> : <div className="min-w-0">{column.header}</div>}</div>{hasSubHeaders && <div className="flex w-full flex-1">{column.subHeaders.map((subHeader_1, index) => <div key={`${column.key}-${index}`} className="border-table-cell-border flex min-w-0 items-center border-r px-4 py-3 last:border-r-0" style={{
              flex: `${getWidth(subHeader_1.widthPercent) / subHeadersWidth * 100} 1 0%`
            }}>{typeof subHeader_1.title === "string" ? <Tooltip mode="overflow" label={subHeader_1.title}><span className="block truncate">{subHeader_1.title}</span></Tooltip> : subHeader_1.title}</div>)}</div>}</div>;
      };
      $[3] = totalWidth;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = columns.map(t2);
    $[0] = columns;
    $[1] = totalWidth;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="border-table-cell-border tablet:flex hidden items-stretch border-b">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function _temp4(total, subHeader_0) {
  return total + getWidth(subHeader_0.widthPercent);
}
function _temp3(subHeader) {
  return !!subHeader.title;
}
function MobileRows(t0) {
  const $ = _c(5);
  const {
    columns,
    data
  } = t0;
  let t1;
  if ($[0] !== columns || $[1] !== data) {
    t1 = data ? data.items.map(row => <Table.Mobile.Card key={row.id}>{columns.map(column => <MobileColumn key={column.key} column={column} row={row} />)}</Table.Mobile.Card>) : Array.from({
      length: SKELETON_ROW_COUNT
    }, (_, rowIndex) => <Table.Mobile.Card key={rowIndex}>{columns.map(_temp5)}</Table.Mobile.Card>);
    $[0] = columns;
    $[1] = data;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <Table.Mobile.Root className="tablet:hidden"><Table.Mobile.Body>{t1}</Table.Mobile.Body></Table.Mobile.Root>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function _temp5(column_0) {
  return <Table.Mobile.LoaderCell key={column_0.key} />;
}
function MobileColumn(t0) {
  const $ = _c(19);
  const {
    column,
    row
  } = t0;
  let t1;
  if ($[0] !== column.subHeaders) {
    t1 = column.subHeaders.some(_temp6);
    $[0] = column.subHeaders;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isGroup = t1;
  let t2;
  if ($[2] !== column.header || $[3] !== isGroup) {
    t2 = isGroup && <MobileColumnName>{column.header}</MobileColumnName>;
    $[2] = column.header;
    $[3] = isGroup;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== column.header || $[6] !== column.key || $[7] !== column.subHeaders || $[8] !== isGroup || $[9] !== row) {
    let t4;
    if ($[11] !== column.header || $[12] !== column.key || $[13] !== isGroup || $[14] !== row) {
      t4 = (subHeader_0, index) => {
        const key = `${column.key}-${index}`;
        if (subHeader_0.onRenderSmall) {
          return <React.Fragment key={key}>{subHeader_0.onRenderSmall(row)}</React.Fragment>;
        }
        return <Table.Mobile.Cell key={key} label={isGroup ? subHeader_0.title : <MobileColumnName>{column.header}</MobileColumnName>}>{subHeader_0.onRender?.(row) ?? subHeader_0.title}</Table.Mobile.Cell>;
      };
      $[11] = column.header;
      $[12] = column.key;
      $[13] = isGroup;
      $[14] = row;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    t3 = column.subHeaders.map(t4);
    $[5] = column.header;
    $[6] = column.key;
    $[7] = column.subHeaders;
    $[8] = isGroup;
    $[9] = row;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[16] !== t2 || $[17] !== t3) {
    t4 = <div className="flex flex-col gap-2">{t2}{t3}</div>;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
}

/**
 * The name of a desktop column, reused as the name of its mobile group or of its single row. It
 * keeps the uppercase of the table head, which is what tells a group name apart from the
 * sentence-case labels of the sub-columns listed under it.
 */
function _temp6(subHeader) {
  return !!subHeader.title;
}
function MobileColumnName(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <span className="text-table-header-text uppercase">{children}</span>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function DesktopRows(t0) {
  const $ = _c(6);
  const {
    columns,
    data,
    totalWidth
  } = t0;
  let t1;
  if ($[0] !== columns || $[1] !== data || $[2] !== totalWidth) {
    t1 = data ? data.items.map(row => <div key={row.id} className="border-table-cell-border flex border-b last:border-b-0">{columns.map(column => <DesktopColumn key={column.key} column={column} row={row} totalWidth={totalWidth} />)}</div>) : Array.from({
      length: SKELETON_ROW_COUNT
    }, (_, rowIndex) => <div key={rowIndex} className="border-table-cell-border flex border-b last:border-b-0">{columns.map(column_0 => <div key={column_0.key} className="px-4 py-3" style={{
        flex: `${getWidth(column_0.widthPercent) / totalWidth * 100} 1 0%`
      }}><Skeleton /></div>)}</div>);
    $[0] = columns;
    $[1] = data;
    $[2] = totalWidth;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <div className="tablet:block hidden">{t1}</div>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function DesktopColumn(t0) {
  const $ = _c(14);
  const {
    column,
    row,
    totalWidth
  } = t0;
  const subHeadersWidth = column.subHeaders.reduce(_temp7, 0);
  const t1 = `${getWidth(column.widthPercent) / totalWidth * 100} 1 0%`;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      flex: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== column.key || $[3] !== column.subHeaders || $[4] !== row || $[5] !== subHeadersWidth) {
    let t4;
    if ($[7] !== column.key || $[8] !== row || $[9] !== subHeadersWidth) {
      t4 = (subHeader_0, index) => <div key={`${column.key}-${index}`} className={twJoin("border-table-cell-border text-text-tertiary flex min-w-0 items-center border-r px-4 py-3 text-base last:border-r-0", subHeader_0.align ?? "justify-start")} style={{
        flex: `${getWidth(subHeader_0.widthPercent) / subHeadersWidth * 100} 1 0%`
      }}><div className="min-w-0 truncate">{subHeader_0.onRender?.(row) ?? subHeader_0.title}</div></div>;
      $[7] = column.key;
      $[8] = row;
      $[9] = subHeadersWidth;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    t3 = column.subHeaders.map(t4);
    $[2] = column.key;
    $[3] = column.subHeaders;
    $[4] = row;
    $[5] = subHeadersWidth;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[11] !== t2 || $[12] !== t3) {
    t4 = <div className="border-table-cell-border flex min-w-0 border-r last:border-r-0" style={t2}>{t3}</div>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
function _temp7(total, subHeader) {
  return total + getWidth(subHeader.widthPercent);
}
export default TabTable;